import React, { useEffect, useState } from 'react';
import { Logo } from '@insight-health-ai/roseate/components';
import { Modal as AntModal, Radio } from 'antd';
import { Form, Formik } from 'formik';

import { koosScoreData } from 'src/utilities/koosScore';
import { hoosScoreData } from 'src/utilities/hoosScore';
import { toast } from '@insight-health-ai/roseate/components/toast';
import { CommonFormProps } from '.';
import { analytics } from 'src/services/analytics';
import { getAppointmentDetailsForAnalytics } from 'src/utils';

type Props = CommonFormProps;

export const OOS = ({ isOpen, form, onSave, appointment, loading }: Props) => {
  const [selectedOptions, setSelectedOptions] = useState<any[]>([]);
  const [totalOssQuestions, settotalOssQuestions] = useState(0);
  const formType = form?.form_type;
  const oos = (form?.content as { table_content: any[] })?.table_content;

  const handleSubmit = async () => {
    const allQuestionsAnswered = totalOssQuestions === totalQuestionsAnswered;
    if (allQuestionsAnswered) {
      analytics.track(
        `Clicked Submit In ${formType} Form`,
        getAppointmentDetailsForAnalytics(appointment!)
      );
      await onSave({
        response: {
          'Raw summed score': total,
          'Interval score': intervalscore,
          options: selectedOptions,
        },
        form_type: formType,
      });
    } else {
      toast.error('Please fill all the fields before proceeding');
    }
  };
  const calculateKOOS = (answer: any) => {
    const KOOSvalue =
      answer === 'None'
        ? 0
        : answer === 'Mild'
          ? 1
          : answer === 'Moderate'
            ? 2
            : answer === 'Severe'
              ? 3
              : answer === 'Extreme'
                ? 4
                : '';
    return KOOSvalue;
  };

  const handleOptionChange = (field: any, question: any, option: any) => {
    const individualKOOS = calculateKOOS(option);
    const newAnswer = {
      section_header: field.title,
      question: question.title,
      answer: option,
      value: individualKOOS,
      questionNumber: question.number,
    };

    // Check if the question number already exists in the array
    const existingIndex = selectedOptions.findIndex(
      (item) => item.questionNumber === question.number
    );

    if (existingIndex !== -1) {
      const updatedOptions = [...selectedOptions] as any[];
      updatedOptions[existingIndex] = newAnswer;
      setSelectedOptions(updatedOptions);
    } else {
      setSelectedOptions((prevOptions) => [...prevOptions, newAnswer]);
    }
  };

  const total = selectedOptions.reduce((acc, option) => acc + option.value, 0);
  const [intervalscore, setIntervalScore] = useState(0);

  function getIntervalScore(total: number) {
    const scoreData = formType === 'KOOS' ? koosScoreData : hoosScoreData;
    const scoreObject = scoreData.find((item) => item.raw_score === total);
    if (scoreObject) {
      setIntervalScore(scoreObject.interval_score);
      return scoreObject.interval_score;
    }
    return null;
  }

  function payload() {
    const intervalScores =
      formType === 'KOOS'
        ? koosScoreData.map((item) => getIntervalScore(total))
        : hoosScoreData.map((item) => getIntervalScore(total));
  }
  useEffect(() => {
    payload();
  }, [selectedOptions, koosScoreData, hoosScoreData]);

  useEffect(() => {
    let totals = 0;
    oos.forEach((section) => {
      if (section.section_content && section.section_content.length > 0) {
        totals += section.section_content.length;
      }
    });
    settotalOssQuestions(totals);
  }, [oos]);

  let totalQuestionsAnswered = 0;
  const sections: any = {};
  selectedOptions.forEach((option: any) => {
    if (!sections[option.section_header]) {
      sections[option.section_header] = [];
    }
    sections[option.section_header].push(option);
  });
  for (const section in sections) {
    let sectionScore = 0;
    const numQuestions = sections[section].length;
    totalQuestionsAnswered += numQuestions;
    sections[section].forEach((option: any) => {
      sectionScore += calculateKOOS(option.answer) as number;
    });
  }
  const renderFields = (fields: any) => {
    return fields?.map((field: any, index: any) => (
      <div key={index}>
        <div
          className={
            field.type === 'section_header'
              ? 'section-heading-text'
              : field.type === 'form_header'
                ? 'heading-text'
                : ''
          }
        >
          {field.title}
        </div>
        <div
          className={
            field.type === 'section_header'
              ? 'subheading-text'
              : field.type === 'form_header'
                ? 'instruction-text'
                : ''
          }
        >
          {field.sub_title.includes('Instructions:') ||
          field.sub_title.includes('how much they bother you') ? (
            <>
              {field.sub_title
                .split(/(Instructions:|how much they bother you.)/)
                .map((part: any, index: any) => (
                  <React.Fragment key={index}>
                    {index % 2 === 1 && <b>{part}</b>}
                    {index % 2 === 0 && part}
                  </React.Fragment>
                ))}
            </>
          ) : (
            field.sub_title
          )}
        </div>
        {/* Render questions if present */}
        {field.question && (
          <div>
            {field.question.map((question: any, qIndex: any) => (
              <div key={qIndex}>
                <p className={question.type === 'label' ? 'ques-text' : ''}>
                  {question.title}
                  <span className="mandatory-star">*</span>
                </p>
                {question.options && (
                  <div>
                    {question.options && (
                      <div>
                        <Radio.Group
                          value={
                            selectedOptions.find(
                              (option: any) =>
                                option.questionNumber === question.number
                            )?.answer
                          }
                          onChange={(e) =>
                            handleOptionChange(field, question, e.target.value)
                          }
                        >
                          {question.options.map((option: any, oIndex: any) => (
                            <Radio key={oIndex} value={option.optionText}>
                              {option.optionText}
                            </Radio>
                          ))}
                        </Radio.Group>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    ));
  };

  const [fields, setFields] = useState<any[]>([]);

  useEffect(() => {
    const transformedArray = oos?.map((section) => {
      const sectionInfo = {
        title: section?.section_header || section?.form_heading,
        type: section?.type,
        sub_title: section?.section_sub_header || section?.form_instructions,
        question: section?.section_content?.map((question: any) => {
          const questionInfo = {
            number: question?.question_no,
            type: question?.type,
            title: question?.question,
            options: question?.question_options?.map((option: any) => ({
              optionText: option?.option_text,
              value: option?.value,
              type: option?.input_type,
            })),
          };
          return questionInfo;
        }),
      };
      return sectionInfo;
    });

    setFields(transformedArray);
  }, []);

  return (
    <AntModal
      open={isOpen}
      className={'PFDI_modal'}
      okText="Submit"
      closable={false}
      centered={true}
      footer={null}
    >
      <div className="modal____body">
        <div className="logo-container">
          <Logo className="h-8" />
        </div>
        {/** @ts-ignore */}
        <Formik>
          {() => (
            <Form className="modal__form font-sans">
              {renderFields(fields)}
            </Form>
          )}
        </Formik>
        <div>
          <button
            type="submit"
            onClick={() => handleSubmit()}
            disabled={loading}
            className="form_submit"
          >
            Submit
          </button>
        </div>
      </div>
    </AntModal>
  );
};
